import React, { useRef, useEffect, useState, useImperativeHandle, forwardRef } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import TextEditor from "./TextEditor";

const FaqComponent = forwardRef(({ faqs, setFaqs, addFaq }, ref) => {
  const [isSourceViews, setIsSourceViews] = useState({});
  const faqItemRefs = useRef([]);
  
  const handleFaqChange = (index, field, value) => {
    const newFaqs = [...faqs];
    newFaqs[index][field] = value;
    setFaqs(newFaqs);
  };

  const removeFaq = (index) => {
    setFaqs(faqs.filter((_, i) => i !== index));
    faqItemRefs.current = faqItemRefs.current.filter((_, i) => i !== index);
  };

  const handleFaqContentSave = (index, contentRef) => {
    // This function will be called when the editor auto-saves
    if (contentRef && contentRef.current) {
      const content = contentRef.current.innerHTML;
      handleFaqChange(index, "content", content);
    }
  };

  const setIsSourceView = (index, value) => {
    setIsSourceViews(prev => ({
      ...prev,
      [index]: value
    }));
  };

  // Expose a method to sync all FAQ contents before saving
  useImperativeHandle(ref, () => ({
    syncAllFaqContents: () => {
      faqItemRefs.current.forEach((itemRef, index) => {
        if (itemRef && itemRef.syncContent) {
          itemRef.syncContent();
        }
      });
    }
  }));

  return (
    <div className="mt-2">
      {faqs.map((faq, index) => (
        <FaqItem
          key={index}
          ref={(el) => (faqItemRefs.current[index] = el)}
          faq={faq}
          index={index}
          handleFaqChange={handleFaqChange}
          addFaq={addFaq}
          removeFaq={removeFaq}
          handleFaqContentSave={handleFaqContentSave}
          isSourceView={isSourceViews[index] || false}
          setIsSourceView={setIsSourceView}
        />
      ))}
    </div>
  );
});

// Separate component for each FAQ item to manage its own ref
const FaqItem = forwardRef(({ faq, index, handleFaqChange, addFaq, removeFaq, handleFaqContentSave, isSourceView, setIsSourceView }, ref) => {
  const contentEditableRef = useRef(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    // Initialize the editor content with the existing FAQ content only once
    if (contentEditableRef.current && !initialized) {
      if (faq.content) {
        contentEditableRef.current.innerHTML = faq.content;
      }
      setInitialized(true);
    }
  }, [initialized]);

  // Create a wrapper function that updates the FAQ state with current editor content
  const handleEditorChange = () => {
    syncContent();
  };

  const syncContent = () => {
    if (contentEditableRef.current) {
      // Clean up the content (remove hover buttons like in the main blog editor)
      let tempElement = document.createElement("div");
      tempElement.innerHTML = contentEditableRef.current.innerHTML;
      let hoverButtons = tempElement.querySelectorAll(".hover-buttons");
      hoverButtons.forEach((button) => button.parentNode.removeChild(button));
      let cleanedHTML = tempElement.innerHTML;
      
      // Update the FAQ content immediately
      handleFaqChange(index, "content", cleanedHTML);
    }
  };

  // Expose syncContent method to parent
  useImperativeHandle(ref, () => ({
    syncContent
  }));

  return (
    <div className="faq border rounded p-3 mb-4 bg-light">
      <Form.Group className="mb-3 question">
        <Row>
          <Col xs={11}>
            <Form.Label className="fw-bold">Question {index + 1}</Form.Label>
            <Form.Control
              as="textarea"
              rows={1}
              value={faq.question}
              onChange={(e) =>
                handleFaqChange(index, "question", e.target.value)
              }
            />
          </Col>
          <Col xs={1} className="d-flex align-items-center justify-content-end gap-2">
            <Button
              variant="success"
              onClick={() => addFaq(index)}
              size="sm"
            >
              +
            </Button>
            <Button 
              variant="danger" 
              onClick={() => removeFaq(index)}
              size="sm"
            >
              X
            </Button>
          </Col>
        </Row>
      </Form.Group>
      <Form.Group className="mb-0 content">
        <Form.Label className="fw-bold">Answer {index + 1}</Form.Label>
        <TextEditor
          contentEditableRef={contentEditableRef}
          createBlogFn={handleEditorChange}
          localStorageKey={`admin_faq_${index}`}
          isSourceView={isSourceView}
          setIsSourceView={(value) => setIsSourceView(index, value)}
        />
      </Form.Group>
    </div>
  );
});

export default FaqComponent;
